import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Spin, Switch, Space, InputNumber } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { createArea, updateArea, getAreaById, Area } from '../../services/api';
import './styles.css';

const { Title } = Typography;

interface ProjectFormProps {
  mode: 'create' | 'edit';
}

const ProjectForm: React.FC<ProjectFormProps> = ({ mode }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isEditMode = mode === 'edit';
  const areaId = isEditMode ? Number(id) : undefined;

  useEffect(() => {
    if (isEditMode && areaId) {
      fetchAreaDetails(areaId);
    }
  }, [isEditMode, areaId]);

  const exDataToPairs = (exData: Record<string, any> | null | undefined): Array<{ key: string; value: string }> => {
    if (!exData || typeof exData !== 'object') return [];
    return Object.entries(exData).map(([key, value]) => ({ key, value: String(value ?? '') }));
  };

  const pairsToExData = (pairs: Array<{ key: string; value: string }> | undefined): Record<string, string> => {
    const result: Record<string, string> = {};
    if (pairs) {
      pairs.forEach(pair => {
        if (pair.key.trim() !== '') {
          result[pair.key.trim()] = pair.value;
        }
      });
    }
    return result;
  };

  const fetchAreaDetails = async (id: number) => {
    try {
      setLoading(true);
      const response = await getAreaById(id);
      if (response.data.code === 0 && response.data.data) {
        const areaData = response.data.data;
        form.setFieldsValue({
          title: areaData.title,
          sort: areaData.sort,
          isAuth: areaData.isAuth === 1,
          status: areaData.status === 1,
          exData: exDataToPairs(areaData.exData),
        });
      } else {
        message.error(response.data.msg || '获取协议详情失败');
        navigate('/projects');
      }
    } catch (error) {
      console.error('获取协议详情错误:', error);
      message.error('获取协议详情失败，请检查网络连接后重试');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);

      const { title, sort, isAuth, status, exData } = values;
      const areaData = {
        title: title?.trim(),
        sort: Number(sort),
        isAuth: isAuth ? 1 : 0,
        status: status ? 1 : 0,
        exData: pairsToExData(exData),
      };

      let response;

      if (isEditMode && areaId) {
        response = await updateArea(areaId, areaData);
      } else {
        response = await createArea(areaData);
      }

      if (response.data.code === 0) {
        message.success(isEditMode ? '协议更新成功' : '协议创建成功');
        navigate('/projects');
      } else {
        message.error(response.data.msg || (isEditMode ? '更新协议失败' : '创建协议失败'));
      }
    } catch (error) {
      console.error(isEditMode ? '更新协议错误:' : '创建协议错误:', error);
      message.error(isEditMode ? '更新协议失败，请检查网络连接后重试' : '创建协议失败，请检查网络连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="project-form-container">
      <Card className="project-form-card">
        <div className="project-form-header">
          <Title level={4}>{isEditMode ? '编辑协议' : '创建协议'}</Title>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/projects')}
          >
            返回列表
          </Button>
        </div>

        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              title: '',
              sort: 0,
              isAuth: false,
              status: true,
              exData: []
            }}
            className="project-form"
          >
            <Form.Item
              name="title"
              label="协议名称"
              rules={[{ required: true, message: '请输入协议名称' }]}
            >
              <Input placeholder="请输入协议名称" />
            </Form.Item>

            <Form.Item
              name="sort"
              label="排序"
              rules={[{ required: true, message: '请输入排序' }]}
            >
              <InputNumber placeholder="请输入排序" style={{ width: '100%' }} min={0} />
            </Form.Item>

            <Form.Item
              name="isAuth"
              label="是否授权"
              valuePropName="checked"
            >
              <Switch checkedChildren="是" unCheckedChildren="否" />
            </Form.Item>

            <Form.Item
              name="status"
              label="状态"
              valuePropName="checked"
            >
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>

            <Form.Item label="扩展数据（exData）">
              <Form.List name="exData">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(field => (
                      <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                        <Form.Item
                          name={[field.name, 'key']}
                          rules={[{ required: true, message: '请输入键' }]}
                          noStyle
                        >
                          <Input placeholder="键" />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'value']}
                          noStyle
                        >
                          <Input placeholder="值" />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                      </Space>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                      block
                    >
                      添加扩展字段
                    </Button>
                  </>
                )}
              </Form.List>
            </Form.Item>

            <Form.Item className="form-actions">
              <Space>
                <Button onClick={() => navigate('/projects')}>取消</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  icon={<SaveOutlined />}
                >
                  {isEditMode ? '更新' : '创建'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </div>
  );
};

export default ProjectForm; 